import clsx from 'clsx';
import type { HTMLAttributes, Ref } from 'react';

import { Icon } from '../icon';
import { Checkbox } from '../checkbox';
import { ColorIndicator } from '../color-indicator';

import styles from './combobox-option.module.scss';

export const ComboboxOption = (props: ComboboxOptionProps) => {
  const {
    ref,
    selected,
    excluded,
    onExclude,
    showExclude,
    highlighted,
    intermediate,
    multiple = true,
    data: { name, color },
    ...rest
  } = props;

  return (
    <ul
      {...rest}
      ref={ref}
      title={name}
      role="option"
      aria-label={name}
      aria-selected={selected}
      className={clsx(styles.comboboxOption, {
        [styles.selected]: selected,
        [styles.excluded]: excluded,
        [styles.excludable]: showExclude,
        [styles.highlighted]: highlighted,
      })}
    >
      {multiple && (
        <li className={styles.cbCheckboxHolder}>
          <Checkbox tabIndex={-1} intermediate={intermediate} checked={!intermediate && selected} />
        </li>
      )}
      <li className={styles.content}>
        {!!color && <ColorIndicator size="small" color={color} data-testid="color-indicator" />}
        <span className="ellipsis-text">{name}</span>
      </li>
      {!multiple && (
        <div className={styles.check}>
          <Icon size={20} type="tick-02" data-testid="single-selected" />
        </div>
      )}
      {(showExclude || excluded) && (
        <li
          className={styles.excludeButton}
          onClick={(e) => {
            e.stopPropagation();
            onExclude?.();
          }}
        >
          {excluded ? 'Excluded' : 'Exclude'}
        </li>
      )}
    </ul>
  );
};

export interface ComboboxData {
  id: string;
  name: string;
  color?: string;
}

export interface ComboboxOptionProps extends HTMLAttributes<HTMLUListElement> {
  selected: boolean;
  excluded?: boolean;
  showExclude?: boolean;
  multiple?: boolean;
  data: ComboboxData;
  highlighted?: boolean;
  intermediate?: boolean;
  ref?: Ref<HTMLUListElement>;
  onExclude?: () => void;
}
